//PaidSignup.jsx - For users coming from successful Stripe payments
import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { createUserWithEmailAndPassword, fetchSignInMethodsForEmail, signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../../config/firebase";
import { createUserDocument, getUserByEmail } from "../../services/firestoreService";
import {
  verifyCheckoutSession,
  markSessionAsUsed,
} from "../../services/stripeService";
import RouteProtector from "../../components/RouteProtector";
import "./Signup.css";

const PaidSignup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    firstName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [isVerifying, setIsVerifying] = useState(true);
  const [sessionId, setSessionId] = useState(null);
  const [showExistingAccountFlow, setShowExistingAccountFlow] = useState(false);
  const [existingAccountEmail, setExistingAccountEmail] = useState("");

  // Verify Stripe session on component mount
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      verifyStripeSession(sessionId);
    } else {
      setError("No payment session found. Please try your purchase again.");
      setIsVerifying(false);
    }
  }, [searchParams]);

  // Verify Stripe checkout session
  const verifyStripeSession = async (sessionId) => {
    try {
      const result = await verifyCheckoutSession(sessionId);
      if (result.verified) {
        setSubscriptionData(result.subscription);
        setSessionId(result.sessionId);
        // Pre-fill email if available from Stripe
        if (result.subscription.customerEmail) {

          setFormData((prev) => ({
            ...prev,
            email: result.subscription.customerEmail,
          }));
          
          // Check if this email already exists in Firebase Auth

          await checkForExistingAccount(result.subscription.customerEmail);
        } else {

        }
      } else {
        setError("Payment verification failed. Please contact support.");
      }
    } catch (error) {


      // Handle specific error types
      if (error.message.includes("expired")) {
        setError(
          "Your payment session has expired. Please complete your purchase again.",
        );
      } else if (error.message.includes("already used")) {
        setError(
          "This payment has already been used to create an account. Please sign in instead.",
        );
      } else {
        setError(
          "Payment verification failed. Please try again or contact support.",
        );
      }
    } finally {
      setIsVerifying(false);
    }
  };

  // Check if account already exists for this email
  const checkForExistingAccount = async (email) => {
    try {

      
      // First check Firebase Auth for active accounts
      const signInMethods = await fetchSignInMethodsForEmail(auth, email);

      
      if (signInMethods && signInMethods.length > 0) {
        // Active Firebase Auth account exists
        setExistingAccountEmail(email);
        setShowExistingAccountFlow(true);

        return true;
      }
      
      // If no Firebase Auth account, check server for user records (expired trials, etc.)

      const response = await fetch(`/api/check-user-exists?email=${encodeURIComponent(email)}`);
      const userCheck = await response.json();
      
      if (userCheck.exists) {
        // User record exists in Firestore but no active auth account
        // This happens when trial expired or account was deleted
        setExistingAccountEmail(email);
        setShowExistingAccountFlow(true);

        return true;
      }
      

      return false;
      
    } catch (error) {

      
      // If auth/configuration-not-found or similar, assume account doesn't exist
      if (error.code === 'auth/configuration-not-found' || 
          error.code === 'auth/invalid-api-key' ||
          error.message.includes('configuration')) {

        return false;
      }
      
      // For other errors, continue with normal signup flow

      return false;
    }
  };

  // Handle existing account login with subscription upgrade
  const handleExistingAccountLogin = async (e) => {
    e.preventDefault();
    setIsAuthenticating(true);
    setError(null);

    try {
      // Sign in with existing credentials
      const userCredential = await signInWithEmailAndPassword(auth, existingAccountEmail, formData.password);
      const user = userCredential.user;
      
      // Upgrade expired trial to paid subscription

      sessionStorage.setItem('upgradeInProgress', 'true');
      
      const upgradeResponse = await fetch('/api/upgrade-trial-to-paid', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await user.getIdToken()}`
        },
        body: JSON.stringify({
          sessionId: sessionId,
          subscriptionData: subscriptionData
        })
      });
      
      if (!upgradeResponse.ok) {
        throw new Error('Failed to upgrade subscription');
      }
      

      
      // Clear upgrade flag
      sessionStorage.removeItem('upgradeInProgress');
      
      // Force a small delay to ensure backend has processed the upgrade
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mark the session as used to prevent reuse
      await markSessionAsUsed(sessionId, user.uid, user.email);
      
      // Navigate to dashboard - user is now on paid plan
      navigate("/dashboard");
    } catch (error) {

      
      // If user-not-found, the account existed in Firestore but not in Auth (expired trial)
      if (error.code === "auth/user-not-found") {
        try {

          // Create new Firebase Auth account
          const newUserCredential = await createUserWithEmailAndPassword(auth, existingAccountEmail, formData.password);
          const newUser = newUserCredential.user;
          
          // Upgrade expired trial to paid subscription

          sessionStorage.setItem('upgradeInProgress', 'true');
          
          const upgradeResponse = await fetch('/api/upgrade-trial-to-paid', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${await newUser.getIdToken()}`
            },
            body: JSON.stringify({
              sessionId: sessionId,
              subscriptionData: subscriptionData
            })
          });
          
          if (!upgradeResponse.ok) {
            throw new Error('Failed to upgrade recreated account subscription');
          }
          

          
          // Clear upgrade flag
          sessionStorage.removeItem('upgradeInProgress');
          
          // Force a small delay to ensure backend has processed the upgrade
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Mark the session as used
          await markSessionAsUsed(sessionId, newUser.uid, newUser.email);
          
          // Navigate to dashboard (existing Firestore data will be preserved and upgraded)
          navigate("/dashboard");
          return;
        } catch (recreateError) {

          // Clear upgrade flag on error
          sessionStorage.removeItem('upgradeInProgress');
          setError('Error upgrading your trial account. Please contact support.');
          return;
        }
      }
      
      if (error.code === "auth/wrong-password") {
        setError("Incorrect password. Please try again.");
      } else if (error.code === "auth/too-many-requests") {
        setError("Too many failed attempts. Please try again later.");
      } else {
        setError("Login failed. Please check your password and try again.");
      }
      
      // Clear upgrade flag on any error
      sessionStorage.removeItem('upgradeInProgress');
    } finally {
      setIsAuthenticating(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Handle account creation
  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    setIsAuthenticating(true);
    setError(null);

    // Validation
    if (!formData.firstName.trim()) {
      setError("Please enter your first name.");
      setIsAuthenticating(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match. Please try again.");
      setIsAuthenticating(false);
      return;
    }

    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters long.");
      setIsAuthenticating(false);
      return;
    }

    if (!subscriptionData) {
      setError("Subscription data not found. Please contact support.");
      setIsAuthenticating(false);
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password,
      );
      const user = userCredential.user;

      // Create user document with properly formatted subscription data for paid users
      const paidSubscriptionData = {
        type: 'paid',
        plan: subscriptionData.plan || 'pro',
        status: 'active',
        customerId: subscriptionData.customerId,
        subscriptionId: subscriptionData.subscriptionId
      };
      
      await createUserDocument(user, formData.firstName.trim(), paidSubscriptionData);

      // Wait for Firestore to propagate the document
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Mark the session as used to prevent reuse
      await markSessionAsUsed(sessionId, user.uid, user.email);

      navigate("/dashboard");
    } catch (error) {

      let errorMessage = "An error occurred during sign up. Please try again.";

      if (error.code === "auth/email-already-in-use") {
        errorMessage =
          "This email is already registered. Please sign in instead.";
      } else if (error.code === "auth/invalid-email") {
        errorMessage = "Please enter a valid email address.";
      } else if (error.code === "auth/weak-password") {
        errorMessage =
          "Password is too weak. Please choose a stronger password.";
      }

      setError(errorMessage);
    } finally {
      setIsAuthenticating(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="signup-container">
        <div className="signup-card">
          <div className="signup-header">
            <img
              src="/You_Create_Logo_WEB.png"
              alt="YouCreate Logo"
              className="signup-logo"
            />
            <h2>Verifying Payment...</h2>
            <p>Please wait while we verify your subscription.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <RouteProtector requiresSession={true}>
      <div className="signup-container">
        <div className="signup-card">
        <div className="signup-header">
          <img
            src="/You_Create_Logo_WEB.png"
            alt="YouCreate Logo"
            className="signup-logo"
          />
          <h2>Your payment was successful!</h2>
          <br></br>
          <p>
            {showExistingAccountFlow
              ? "We found an existing account with this email. Please sign in to upgrade your subscription."
              : "Welcome to YouCreate! Please fill in your details to access your dashboard."}
          </p>
        </div>

        {subscriptionData && (
          <div className="subscription-status">
            <h3>
              ✓{" "}
              {subscriptionData.plan 
                ? subscriptionData.plan.charAt(0).toUpperCase() + subscriptionData.plan.slice(1)
                : "Subscription"}{" "}
              Plan Active
            </h3>
            <p>
              {showExistingAccountFlow
                ? "Sign in to upgrade your existing account"
                : "Payment verified - complete your account setup"}
            </p>
          </div>
        )}

        {showExistingAccountFlow ? (
          <form onSubmit={handleExistingAccountLogin} className="signup-form">
            {error && <div className="error-message">{error}</div>}

            <div className="input-group">
              <input
                type="email"
                name="email"
                placeholder="Email Address"
                value={existingAccountEmail}
                disabled
                className="signup-input"
                style={{ backgroundColor: "#f5f5f5", color: "#666" }}
              />
            </div>

            <div className="input-group">
              <input
                type="password"
                name="password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleInputChange}
                required
                className="signup-input"
              />
            </div>

            <button
              type="submit"
              disabled={isAuthenticating}
              className="signup-button"
            >
              {isAuthenticating ? "Signing In..." : "Sign In & Upgrade"}
            </button>

            <div className="signup-info">
              <p>
                This will upgrade your existing account to the{" "}
                <strong>
                  {subscriptionData?.plan.charAt(0).toUpperCase() +
                    subscriptionData?.plan.slice(1)}
                </strong>{" "}
                plan and preserve all your assets and canvases.
              </p>
            </div>


          </form>
        ) : (
          <form onSubmit={handleEmailSignUp} className="signup-form">
            {error && <div className="error-message">{error}</div>}

            <div className="input-group">
              <input
                type="text"
                name="firstName"
                placeholder="First Name"
                value={formData.firstName}
                onChange={handleInputChange}
                required
                className="signup-input"
              />
            </div>

            <div className="input-group">
              <input
                type="email"
                name="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="signup-input"
              />
            </div>

            <div className="input-group">
              <input
                type="password"
                name="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleInputChange}
                required
                className="signup-input"
              />
            </div>

            <div className="input-group">
              <input
                type="password"
                name="confirmPassword"
                placeholder="Confirm Password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
                className="signup-input"
              />
            </div>

            <button
              type="submit"
              disabled={isAuthenticating}
              className="signup-button"
            >
              {isAuthenticating
                ? "Creating Account..."
                : "Complete Account Setup"}
            </button>

            <div className="signin-link">
              <span>Already have an account? </span>
              <Link to="/login">Sign In Here</Link>
            </div>
          </form>
        )}
      </div>
      </div>
    </RouteProtector>
  );
};

export default PaidSignup;